import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { ProductResponse } from "@/app/api/types/product";
import { useProducts, useProduct } from "@/query/hooks/useProducts";
import { UUID } from "crypto";
import { filterProductsBySearch } from "@/lib/utils/productFiltering";
import { tokenMatchFilter } from "@stockline/product-search";
import { PRODUCT_SEARCH_CONFIG } from "@/lib/search/searchConfigs";
import { useTimeSlicedSearch } from "@/hooks/use-time-sliced-search";
import type { StockSummary } from "@/query/hooks/useGlobalStocks";
import type { CustomFieldDefinition } from "@/app/api/types/featureFlags";
import { resolveStockDropdownColumns } from "@/lib/stocks/stockColumns";

const RENDER_PAGE_SIZE = 20;

interface UseProductSelectDataParams {
  value: UUID | null;
  preloadedProduct?: ProductResponse | null;
  priceLookup?: (productId: string) => number | null;
  stockLookup?: (productId: string) => StockSummary | null;
  stockCustomFields?: CustomFieldDefinition[];
  effectiveSearchTerm: string;
  /**
   * Live (un-debounced) search-box value. Only used to derive
   * `isSearchPending`: the spinner must track what the user just typed, not
   * the settled term the results answer.
   */
  rawSearchTerm: string;
  openedByButton: boolean;
  filterInStock: boolean;
  filterPriced: boolean;
  isOpen: boolean;
  isManuallyNavigating: boolean;
  selectedDropdownIndex: number;
  setSelectedDropdownIndex: (idx: number) => void;
  setCurrentProductDetails: (p: ProductResponse | null) => void;
  setSkuInput: (v: string) => void;
  setPreviousSkuValue: (v: string) => void;
  setIsOpen: (v: boolean) => void;
  setSearchTerm: (v: string) => void;
  hasInitializedInput: boolean;
  setHasInitializedInput: (v: boolean) => void;
  observerTarget: React.RefObject<HTMLDivElement | null>;
  /** True when the current search term is the untouched seed from a prior
   * product selection. Routes the filter to `tokenMatchFilter` (no Fuse,
   * no number normalization) since the seed words come from a real product
   * name and don't need typo tolerance — ~30× faster than the typed path. */
  isUntouchedSeed: boolean;
  /**
   * Catalog injected by a caller that already holds it — recap's
   * `ProductLookupsContext`, which `FlowsTable` mounts once per pre-order.
   * MUST already be non-variant-filtered and sleeping-filtered: the hook
   * treats it as the finished picker corpus and applies no further shaping.
   *
   * When present the hook's own `useProducts` subscription is switched off,
   * so N combos on a focus view pay ONE catalog observer, ONE non-variant
   * filter and ONE `useTimeSlicedSearch` index instead of N of each. Same
   * seam as `EntitySelectWithTags`'s `allEntities` override
   * (`components/ui/EntitySelectWithTags.tsx:231`).
   */
  allProductsOverride?: ProductResponse[];
}

export function useProductSelectData(params: UseProductSelectDataParams) {
  const {
    value,
    preloadedProduct,
    priceLookup,
    stockLookup,
    stockCustomFields,
    effectiveSearchTerm,
    rawSearchTerm,
    openedByButton,
    filterInStock,
    filterPriced,
    isOpen,
    isManuallyNavigating,
    selectedDropdownIndex,
    setSelectedDropdownIndex,
    setCurrentProductDetails,
    setSkuInput,
    setPreviousSkuValue,
    setIsOpen,
    setSearchTerm,
    hasInitializedInput,
    setHasInitializedInput,
    observerTarget,
    isUntouchedSeed,
    allProductsOverride,
  } = params;

  // An EMPTY override is NOT an override. The provider can be mounted before
  // its catalog has synced, and an empty array is truthy — taken at face value
  // it would disable the fetch and leave the dropdown permanently blank on a
  // cold start. Fall through to the own-fetch path until it has content.
  const injectedProducts = allProductsOverride?.length
    ? allProductsOverride
    : undefined;

  // Own catalog fetch — disabled when the caller injects one. `useProducts`
  // ignores `include_children` / `pageSize` / `infiniteScroll` (documented
  // no-ops on the global-cache hook); the only options that shape the result
  // are `include_sleeping` and `includeAll`.
  const productsQuery = useProducts({
    include_sleeping: false,
    includeAll: true,
    enabled: !injectedProducts,
  });

  const allProductsWithChildren = injectedProducts ?? productsQuery.data;
  const productsLoading = injectedProducts ? false : productsQuery.isLoading;

  // Render pagination
  const [renderPage, setRenderPage] = useState(0);

  useEffect(() => {
    setRenderPage(0);
  }, [effectiveSearchTerm]);

  // Non-variant products (stable across searches). When the caller injected a
  // catalog it is ALREADY this shape — re-filtering 7k rows per combo per open
  // is exactly the cost this override exists to remove.
  const nonVariantProducts = useMemo(() => {
    if (!isOpen) return [];
    if (injectedProducts) return injectedProducts;
    return allProductsWithChildren.filter(
      (p) => !p.parents || p.parents.length === 0,
    );
  }, [allProductsWithChildren, injectedProducts, isOpen]);

  // Typed-path (button-opened) search runs through the shared time-sliced
  // hook (STK-1399): chunked scan with main-thread yields, eager warm-up on
  // open, per-term aborts — typing never waits on the search. Same machinery
  // as the sender/recipient pickers (`EntitySelectWithTags`). The SKU path
  // (prefix filter) and the seed path (token match) below stay synchronous.
  const trimmedTerm = effectiveSearchTerm.trim();
  const isTypedSearch =
    isOpen && openedByButton && !isUntouchedSeed && trimmedTerm !== "";
  const { results: typedSearchResults } = useTimeSlicedSearch<ProductResponse>({
    corpus: nonVariantProducts,
    searchConfig: PRODUCT_SEARCH_CONFIG,
    enabled: isOpen && openedByButton,
    term: isTypedSearch ? trimmedTerm : "",
  });

  // Last list committed to screen — shown while a fresh search is in flight
  // so a term change never flashes the full catalog.
  const lastShownRef = useRef<ProductResponse[] | null>(null);

  // Filter products by search term — skip when dropdown is closed
  const allProducts = useMemo(() => {
    if (!isOpen) return [];

    if (!openedByButton) {
      return filterProductsBySearch(
        nonVariantProducts,
        effectiveSearchTerm,
        true,
      );
    }

    if (!effectiveSearchTerm || effectiveSearchTerm.trim() === "") {
      return nonVariantProducts;
    }

    // Seed-prefill path: skip the async search entirely. The seed is the
    // exact name/short_name of an already-selected product, so its tokens are
    // guaranteed to be present in the catalog — pure token-match scoring
    // (+2 exact word, +1 prefix word) gives the right ranking with no
    // typo tolerance and no number-normalization, synchronously.
    if (isUntouchedSeed) {
      return tokenMatchFilter(nonVariantProducts, effectiveSearchTerm, {
        textFields: PRODUCT_SEARCH_CONFIG.textFields,
      }).map((r) => r.item);
    }

    // Typed path: async results, keyed by term so a stale reply never renders
    // as current. While in flight, keep the previous list on screen.
    const trimmed = effectiveSearchTerm.trim();
    if (typedSearchResults?.term === trimmed) return typedSearchResults.items;
    return lastShownRef.current ?? nonVariantProducts;
  }, [
    nonVariantProducts,
    effectiveSearchTerm,
    openedByButton,
    isOpen,
    isUntouchedSeed,
    typedSearchResults,
  ]);

  useEffect(() => {
    lastShownRef.current = isOpen ? allProducts : null;
  }, [allProducts, isOpen]);

  // The rendered list answers `typedSearchResults.term`; the user's latest
  // intent is `rawSearchTerm`. Until they meet (debounce settling or a search
  // in flight) the results on screen are stale — surfaced as a spinner in the
  // dropdown's search bar. Suppressed on the seed, SKU, and empty-box paths,
  // which all filter synchronously.
  const trimmedRawTerm = rawSearchTerm.trim();
  const isSearchPending =
    isOpen &&
    openedByButton &&
    !isUntouchedSeed &&
    trimmedRawTerm !== "" &&
    typedSearchResults?.term !== trimmedRawTerm;

  // Fetch selected product details
  const { data: selectedProduct } = useProduct(value?.toString() || "");

  // Stock summary from preloaded product
  const stockSummary = preloadedProduct?.stock_summary
    ? { stocks: preloadedProduct.stock_summary }
    : null;

  // Resolve current product details from multiple sources
  useEffect(() => {
    if (!value) {
      setCurrentProductDetails(null);
      return;
    }

    let found = allProductsWithChildren.find((p) => p.id === value);

    if (!found) {
      for (const product of allProductsWithChildren) {
        if (product.children) {
          const childMatch = product.children.find((c) => c.id === value);
          if (childMatch) {
            found = childMatch;
            break;
          }
        }
      }
    }

    if (found) {
      setCurrentProductDetails(found);
    } else if (preloadedProduct && preloadedProduct.id === value) {
      setCurrentProductDetails(preloadedProduct);
    } else if (selectedProduct) {
      setCurrentProductDetails(selectedProduct);
    }
  }, [
    value,
    selectedProduct,
    allProductsWithChildren,
    preloadedProduct,
    setCurrentProductDetails,
  ]);

  // Initialize SKU input when product details change
  useEffect(() => {
    // Access currentProductDetails indirectly through selectedProduct/allProductsWithChildren
    // to find the current product for SKU initialization
    let currentProduct: ProductResponse | null = null;
    if (value) {
      currentProduct =
        allProductsWithChildren.find((p) => p.id === value) ?? null;
      if (!currentProduct) {
        for (const p of allProductsWithChildren) {
          if (p.children) {
            const child = p.children.find((c) => c.id === value);
            if (child) {
              currentProduct = child;
              break;
            }
          }
        }
      }
      if (!currentProduct && preloadedProduct?.id === value) {
        currentProduct = preloadedProduct;
      }
      if (!currentProduct && selectedProduct) {
        currentProduct = selectedProduct;
      }
    }

    if (currentProduct?.sku && !hasInitializedInput) {
      setSkuInput(currentProduct.sku);
      setPreviousSkuValue(currentProduct.sku);
      setHasInitializedInput(true);
    } else if (!currentProduct?.sku && hasInitializedInput) {
      setSkuInput("");
      setPreviousSkuValue("");
      setHasInitializedInput(false);
    }
  }, [
    value,
    allProductsWithChildren,
    selectedProduct,
    preloadedProduct,
    hasInitializedInput,
    setSkuInput,
    setPreviousSkuValue,
    setHasInitializedInput,
  ]);

  // Reset on value change
  useEffect(() => {
    setHasInitializedInput(false);
    if (!value) {
      setSkuInput("");
      setPreviousSkuValue("");
      setIsOpen(false);
      setSearchTerm("");
      setSelectedDropdownIndex(-1);
    }
  }, [
    value,
    setSkuInput,
    setPreviousSkuValue,
    setIsOpen,
    setSearchTerm,
    setSelectedDropdownIndex,
    setHasInitializedInput,
  ]);

  // Config-driven compact columns for stock display
  const dropdownColumns = useMemo(
    () => resolveStockDropdownColumns(stockCustomFields),
    [stockCustomFields],
  );

  // Filter predicates — shared by the applied filter and the count display so
  // "In stock (n)" / "Priced (n)" and the empty-state undo counts always match
  // exactly what the filters would keep.
  const matchesInStock = useCallback(
    (p: ProductResponse) => {
      if (!stockLookup) return true;
      const s = stockLookup(p.id);
      if (!s) return false;
      return (
        (s.quantity ?? 0) > 0 ||
        dropdownColumns.some((c) => (s.metadata?.[c.key] ?? 0) > 0)
      );
    },
    [stockLookup, dropdownColumns],
  );

  const matchesPriced = useCallback(
    (p: ProductResponse) => (priceLookup ? priceLookup(p.id) != null : true),
    [priceLookup],
  );

  // Apply stock and price filters
  const filteredProducts = useMemo(() => {
    let result = allProducts;
    if (filterInStock && stockLookup) {
      result = result.filter(matchesInStock);
    }
    if (filterPriced && priceLookup) {
      result = result.filter(matchesPriced);
    }
    return result;
  }, [
    allProducts,
    filterInStock,
    stockLookup,
    filterPriced,
    priceLookup,
    matchesInStock,
    matchesPriced,
  ]);

  // Counts driving the header badges and the empty-state undo buttons.
  // Computed off `allProducts` (search matches, pre stock/price filter):
  //  - inStock / priced: how many matches each filter alone would keep.
  //  - withoutInStock / withoutPriced: how many would remain if that one
  //    filter were removed while the other stays as-is — the number the user
  //    "would get" by undoing it. `total` = both filters off.
  const filterCounts = useMemo(() => {
    const total = allProducts.length;
    const inStock = stockLookup
      ? allProducts.filter(matchesInStock).length
      : total;
    const priced = priceLookup
      ? allProducts.filter(matchesPriced).length
      : total;
    const withoutInStock = filterPriced && priceLookup ? priced : total;
    const withoutPriced = filterInStock && stockLookup ? inStock : total;
    return { total, inStock, priced, withoutInStock, withoutPriced };
  }, [
    allProducts,
    stockLookup,
    priceLookup,
    filterInStock,
    filterPriced,
    matchesInStock,
    matchesPriced,
  ]);

  // Client-side render pagination
  const productsToRender = useMemo(() => {
    return filteredProducts.slice(0, (renderPage + 1) * RENDER_PAGE_SIZE);
  }, [filteredProducts, renderPage]);

  const hasMoreRenderPages = productsToRender.length < filteredProducts.length;

  // Grows the RENDERED window as the user scrolls the dropdown. There is no
  // REST page behind this — the catalog arrives in one shot from the global
  // cache — so the sentinel's only job is client-side slicing.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRenderPages) {
          setRenderPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1, rootMargin: "100px" },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget && isOpen) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [isOpen, hasMoreRenderPages, renderPage, observerTarget]);

  // Auto-select first item when products change
  useEffect(() => {
    if (isManuallyNavigating) return;
    if (productsToRender.length > 0 && isOpen) {
      setSelectedDropdownIndex(0);
    }
  }, [
    productsToRender,
    isOpen,
    isManuallyNavigating,
    setSelectedDropdownIndex,
  ]);

  // Children/variants lookup with cache
  const getProductChildren = useMemo(() => {
    const cache = new Map<UUID, ProductResponse[]>();

    return (productId: UUID) => {
      if (cache.has(productId)) {
        return cache.get(productId)!;
      }

      const product = allProductsWithChildren.find((p) => p.id === productId);
      const children = product?.children || [];
      const filtered = children.filter(
        (child) => child.is_user_product !== false,
      );
      cache.set(productId, filtered);
      return filtered;
    };
  }, [allProductsWithChildren]);

  const isEnriched = !!(priceLookup || stockLookup);

  return {
    allProducts,
    allProductsWithChildren,
    productsToRender,
    filteredProducts,
    filterCounts,
    isSearchPending,
    productsLoading,
    hasMoreRenderPages,
    dropdownColumns,
    isEnriched,
    stockSummary,
    getProductChildren,
    renderPage,
    setRenderPage,
  };
}
